/**
 * Debounce Utilities
 *
 * Provides debouncing and throttling utilities for map viewport updates
 * to maintain smooth 60fps performance during pan/zoom operations.
 *
 * Phase 8.1.4: Debounced viewport updates
 */

// ============================================================================
// Types
// ============================================================================

export interface DebounceOptions {
  /** Delay in milliseconds before executing */
  delay: number;
  /** Maximum wait time before forced execution */
  maxWait?: number;
  /** Execute on leading edge instead of trailing */
  leading?: boolean;
  /** Execute on trailing edge (default: true) */
  trailing?: boolean;
}

export interface ThrottleOptions {
  /** Minimum interval between executions in ms */
  interval: number;
  /** Execute on leading edge (default: true) */
  leading?: boolean;
  /** Execute on trailing edge (default: true) */
  trailing?: boolean;
}

export interface DebouncedFunction<TArgs extends unknown[]> {
  (...args: TArgs): void;
  /** Cancel any pending invocation */
  cancel: () => void;
  /** Immediately execute pending invocation */
  flush: () => void;
  /** Check if there's a pending invocation */
  pending: () => boolean;
}

export interface ThrottledFunction<TArgs extends unknown[]> {
  (...args: TArgs): void;
  /** Cancel any pending trailing invocation */
  cancel: () => void;
}

// ============================================================================
// Constants
// ============================================================================

/** Target frame time for 60fps (16.67ms) */
export const FRAME_TIME_60FPS = 1000 / 60; // ~16.67ms

/** Recommended debounce delays for different operations */
export const DEBOUNCE_DELAYS = {
  /** Viewport state updates during pan/zoom */
  VIEWPORT_UPDATE: 16, // One frame at 60fps
  /** GeoJSON regeneration */
  GEOJSON_UPDATE: 32, // Two frames
  /** Cluster recalculation */
  CLUSTER_UPDATE: 50, // ~3 frames
  /** Layer visibility changes */
  LAYER_CHANGE: 100,
  /** Tooltip position updates */
  TOOLTIP_UPDATE: 8, // Half frame
  /** Search/filter operations */
  SEARCH_UPDATE: 150,
} as const;

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Creates a debounced function that delays invoking func until after
 * delay milliseconds have elapsed since the last time it was invoked.
 *
 * @param func - Function to debounce
 * @param options - Debounce options or delay number
 * @returns Debounced function with cancel and flush methods
 *
 * @example
 * ```typescript
 * const debouncedUpdate = debounce(
 *   (viewState: ViewState) => setViewState(viewState),
 *   { delay: 16, maxWait: 100 }
 * );
 *
 * // In onMove handler
 * const handleMove = (e) => debouncedUpdate(e.viewState);
 * ```
 */
export function debounce<TArgs extends unknown[], TResult>(
  func: (...args: TArgs) => TResult,
  options: DebounceOptions | number
): DebouncedFunction<TArgs> {
  const opts: DebounceOptions = typeof options === 'number'
    ? { delay: options }
    : options;

  const {
    delay,
    maxWait,
    leading = false,
    trailing = true,
  } = opts;

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let maxTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: TArgs | null = null;
  let lastCallTime: number | undefined;
  let lastInvokeTime = 0;

  const invokeFunc = () => {
    const args = lastArgs;
    if (args === null) return;
    lastArgs = null;
    lastInvokeTime = Date.now();
    func(...args);
  };

  const startMaxTimer = () => {
    if (maxWait !== undefined && !maxTimeoutId) {
      maxTimeoutId = setTimeout(() => {
        maxTimeoutId = null;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (lastArgs) {
          invokeFunc();
        }
      }, maxWait);
    }
  };

  const debounced = ((...args: TArgs) => {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastCallTime = time;

    if (isInvoking) {
      if (!timeoutId) {
        // First call or after timeout
        if (leading) {
          invokeFunc();
        }
        startMaxTimer();
      }
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = null;
      if (maxTimeoutId) {
        clearTimeout(maxTimeoutId);
        maxTimeoutId = null;
      }
      if (trailing && lastArgs) {
        invokeFunc();
      }
    }, delay);
  }) as DebouncedFunction<TArgs>;

  function shouldInvoke(time: number): boolean {
    const timeSinceLastCall = lastCallTime === undefined ? 0 : time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= delay ||
      timeSinceLastCall < 0 ||
      (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
    );
  }

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (maxTimeoutId) {
      clearTimeout(maxTimeoutId);
      maxTimeoutId = null;
    }
    lastArgs = null;
    lastCallTime = undefined;
  };

  debounced.flush = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (maxTimeoutId) {
      clearTimeout(maxTimeoutId);
      maxTimeoutId = null;
    }
    if (lastArgs) {
      invokeFunc();
    }
  };

  debounced.pending = () => {
    return timeoutId !== null;
  };

  return debounced;
}

/**
 * Creates a throttled function that only invokes func at most once
 * per every interval milliseconds.
 *
 * @param func - Function to throttle
 * @param options - Throttle options or interval number
 * @returns Throttled function
 *
 * @example
 * ```typescript
 * const throttledResize = throttle(
 *   () => recalculateLayout(),
 *   { interval: 100 }
 * );
 *
 * window.addEventListener('resize', throttledResize);
 * ```
 */
export function throttle<TArgs extends unknown[], TResult>(
  func: (...args: TArgs) => TResult,
  options: ThrottleOptions | number
): ThrottledFunction<TArgs> {
  const opts: ThrottleOptions = typeof options === 'number'
    ? { interval: options }
    : options;

  const {
    interval,
    leading = true,
    trailing = true,
  } = opts;

  let lastCallTime: number | undefined;
  let lastArgs: TArgs | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const invokeFunc = () => {
    const args = lastArgs;
    if (args === null) return;
    lastArgs = null;
    lastCallTime = Date.now();
    func(...args);
  };

  const throttled = ((...args: TArgs) => {
    const now = Date.now();
    const timeSinceLastCall = lastCallTime === undefined ? interval : now - lastCallTime;

    lastArgs = args;

    if (timeSinceLastCall >= interval) {
      // Enough time has passed, invoke immediately
      if (leading || lastCallTime !== undefined) {
        invokeFunc();
      } else {
        lastCallTime = now;
      }
    } else if (trailing && !timeoutId) {
      // Schedule trailing call
      timeoutId = setTimeout(() => {
        timeoutId = null;
        if (lastArgs) {
          invokeFunc();
        }
      }, interval - timeSinceLastCall);
    }
  }) as ThrottledFunction<TArgs>;

  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = null;
    lastCallTime = undefined;
  };

  return throttled;
}

/**
 * Creates a request animation frame-based update function
 *
 * Ensures updates happen at most once per animation frame,
 * perfect for smooth 60fps rendering.
 *
 * @param func - Function to schedule
 * @returns RAF-throttled function
 *
 * @example
 * ```typescript
 * const smoothUpdate = rafThrottle((x, y) => {
 *   element.style.transform = `translate(${x}px, ${y}px)`;
 * });
 *
 * element.addEventListener('mousemove', (e) => smoothUpdate(e.clientX, e.clientY));
 * ```
 */
export function rafThrottle<TArgs extends unknown[], TResult>(
  func: (...args: TArgs) => TResult
): ThrottledFunction<TArgs> {
  let rafId: number | null = null;
  let lastArgs: TArgs | null = null;

  const throttled = ((...args: TArgs) => {
    lastArgs = args;

    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (lastArgs) {
          const args = lastArgs;
          lastArgs = null;
          func(...args);
        }
      });
    }
  }) as ThrottledFunction<TArgs>;

  throttled.cancel = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    lastArgs = null;
  };

  return throttled;
}

/**
 * Creates a debounced viewport state update handler
 *
 * Specialized debounce for map viewport updates that:
 * 1. Uses RAF for smooth visual updates
 * 2. Batches rapid state changes
 * 3. Provides maxWait to prevent stale UI
 *
 * @param setState - State setter function
 * @param maxWait - Maximum wait before forced update (default: 100ms)
 * @returns Debounced viewport handler
 */
export function createViewportDebounce<T>(
  setState: (state: T) => void,
  maxWait: number = 100
): DebouncedFunction<[state: T]> {
  console.log('[Debounce] Creating viewport debounce handler with maxWait:', maxWait);

  return debounce(
    (state: T) => {
      console.log('[Debounce] Viewport state update applied');
      setState(state);
    },
    {
      delay: DEBOUNCE_DELAYS.VIEWPORT_UPDATE,
      maxWait,
      leading: false,
      trailing: true,
    }
  );
}

/**
 * Creates an idle callback-based update function
 *
 * Schedules updates during browser idle time, perfect for
 * non-urgent updates that shouldn't block the main thread.
 *
 * @param func - Function to schedule
 * @param timeout - Maximum wait time in ms
 * @returns Idle-scheduled function
 */
export function idleCallback<TArgs extends unknown[], TResult>(
  func: (...args: TArgs) => TResult,
  timeout: number = 1000
): ThrottledFunction<TArgs> {
  type IdleHandle = number | ReturnType<typeof setTimeout>;
  type IdleCallbackDeadline = {
    didTimeout: boolean;
    timeRemaining: () => number;
  };
  type RequestIdle = (
    callback: (deadline: IdleCallbackDeadline) => void,
    options?: { timeout?: number }
  ) => IdleHandle;

  let idleId: IdleHandle | null = null;
  let lastArgs: TArgs | null = null;

  // Fallback for browsers without requestIdleCallback
  const requestIdle: RequestIdle = typeof requestIdleCallback !== 'undefined'
    ? requestIdleCallback
    : (callback) =>
        setTimeout(
          () => callback({ didTimeout: false, timeRemaining: () => 0 }),
          1
        );

  const cancelIdle = (handle: IdleHandle) => {
    if (typeof cancelIdleCallback !== 'undefined' && typeof handle === 'number') {
      cancelIdleCallback(handle);
      return;
    }
    clearTimeout(handle);
  };

  const scheduled = ((...args: TArgs) => {
    lastArgs = args;

    if (idleId === null) {
      idleId = requestIdle(
        () => {
          idleId = null;
          if (lastArgs) {
            const args = lastArgs;
            lastArgs = null;
            func(...args);
          }
        },
        { timeout }
      );
    }
  }) as ThrottledFunction<TArgs>;

  scheduled.cancel = () => {
    if (idleId !== null) {
      cancelIdle(idleId);
      idleId = null;
    }
    lastArgs = null;
  };

  return scheduled;
}

/**
 * Measure debounce effectiveness
 *
 * Returns a wrapper that tracks how many calls were debounced.
 *
 * @param debouncedFn - A debounced function
 * @returns Wrapper with call statistics
 */
export function measureDebounce<TArgs extends unknown[]>(
  debouncedFn: DebouncedFunction<TArgs>
): DebouncedFunction<TArgs> & { getStats: () => { calls: number; executed: number; ratio: number } } {
  let totalCalls = 0;
  let executedCalls = 0;

  const originalFlush = debouncedFn.flush;
  debouncedFn.flush = () => {
    executedCalls++;
    originalFlush();
  };

  const measured = ((...args: TArgs) => {
    totalCalls++;
    // Check if this will actually execute
    if (!debouncedFn.pending()) {
      executedCalls++;
    }
    debouncedFn(...args);
  }) as DebouncedFunction<TArgs> & { getStats: () => { calls: number; executed: number; ratio: number } };

  measured.cancel = debouncedFn.cancel;
  measured.flush = debouncedFn.flush;
  measured.pending = debouncedFn.pending;

  measured.getStats = () => ({
    calls: totalCalls,
    executed: executedCalls,
    ratio: totalCalls > 0 ? executedCalls / totalCalls : 1,
  });

  return measured;
}

export default {
  debounce,
  throttle,
  rafThrottle,
  createViewportDebounce,
  idleCallback,
  measureDebounce,
  DEBOUNCE_DELAYS,
  FRAME_TIME_60FPS,
};
